export interface TicketApiResponse {
    availability?: "SALABLE" | "SOLD_OUT" | "NOT_SALABLE";
    package_product_id?: number;
    package_product_name?: string;
    tickets?: any[];
    price_config?: any[];
    admissions?: any[];
    message?: string;
    addons?: any[];
    async_pricing_mode?: number;
    currency_code?: string;
    display_discount?: boolean;
    locale?: string;
    options?: any[];
    remove_precision?: boolean;
    serviceTimeZone?: string;
}

export interface TicketError {
    type:
        | "MEMBER_EXPIRED"
        | "MEMBER_INACTIVE"
        | "GUEST"
        | "UNAVAILABLE"
        | "SOLD_OUT"
        | "NOT_SALABLE"
        | "NOT_EXISTS"
        | "GENERIC";
}

export interface TicketStateHandlers {
    setSetupError: (error: boolean) => void;
    setTicketSoldOut: (soldOut: boolean) => void;
    setTicketExpired: (expired: boolean) => void;
    setTicketNotExists: (notExists: boolean) => void;
    setMemberExpired?: (expired: boolean) => void;
    setMemberInactive?: (inactive: boolean) => void;
    redirectToLoginPage: () => void;
}

export const getTickets = async (
    endpoint: string,
    accessToken: string,
    setError: (setError: boolean) => void,
    stateHandlers?: TicketStateHandlers,
    memberPageUrl?: string
): Promise<[any[], any[], string, TicketError | null]> => {
    var response = null;
    var statusCode = 200;
    let error: TicketError | null = null;

    try {
        if (accessToken !== null) {
            var bearer = "Bearer " + accessToken;
            const requestOptions: RequestInit = {
                method: "GET",
                headers: {
                    Authorization: bearer,
                },
            };
            const responseRaw = await fetch(endpoint, requestOptions);
            statusCode = responseRaw.status;

            // Handle 401 responses (authentication/authorization errors)
            if (statusCode === 401) {
                let errorResponse;
                try {
                    errorResponse = await responseRaw.json();
                    const errorCode = errorResponse.code || errorResponse.error_code;

                    switch (errorCode) {
                        case 1: // MEMBER_EXPIRED
                            error = {
                                type: "MEMBER_EXPIRED",
                            };

                            if (stateHandlers?.setMemberExpired) {
                                stateHandlers.setMemberExpired(true);
                            } else {
                                stateHandlers?.setSetupError(true);
                            }
                            break;
                        case 2: // MEMBER_INACTIVE
                            error = {
                                type: "MEMBER_INACTIVE",
                            };

                            if (stateHandlers?.setMemberInactive) {
                                stateHandlers.setMemberInactive(true);
                            } else {
                                stateHandlers?.setSetupError(true);
                            }
                            break;

                        case 0: // UNAVAILABLE
                        default:
                            error = {
                                type: "UNAVAILABLE",
                            };

                            if (stateHandlers) {
                                stateHandlers.setSetupError(true);
                            }
                            break;
                    }
                } catch (parseError) {
                    error = {
                        type: "UNAVAILABLE",
                    };

                    if (stateHandlers) {
                        stateHandlers.setSetupError(true);
                    }
                }
                return [[], null, JSON.stringify(errorResponse || {}), error];
            }

            response = await responseRaw.json();
            if (responseRaw.status !== 200) {
                setError(true);
                error = {
                    type: "GENERIC",
                };

                if (stateHandlers) {
                    stateHandlers.setSetupError(true);
                }
                return [[], null, JSON.stringify(response), error];
            }
        } else {
            const responseRaw = await fetch(endpoint);
            statusCode = responseRaw.status;

            if (statusCode === 401) {
                // Handle 401 for non-authenticated requests (GUEST)
                error = {
                    type: "GUEST",
                };

                if (stateHandlers) {
                    stateHandlers.redirectToLoginPage();
                }
                return [[], null, "", error];
            }

            response = await responseRaw.json();
            if (responseRaw.status !== 200) {
                statusCode = responseRaw.status;
                setError(true);
                console.log("Error fetching tickets:", response);
                error = {
                    type: "GENERIC",
                };

                if (stateHandlers) {
                    stateHandlers.setSetupError(true);
                }
                return [[], null, JSON.stringify(response), error];
            }
        }

        const json = statusCode === 200 ? JSON.parse(response) : response;

        // Handle availability status for 200 responses
        if (json.availability) {
            switch (json.availability) {
                case "SOLD_OUT":
                    error = {
                        type: "SOLD_OUT",
                    };

                    if (stateHandlers) {
                        stateHandlers.setTicketSoldOut(true);
                    }
                    return [[], json.admissions, response, error];

                case "NOT_SALABLE":
                    error = {
                        type: "NOT_SALABLE",
                    };

                    if (stateHandlers) {
                        stateHandlers.setTicketExpired(true);
                    }
                    return [[], json.admissions, response, error];

                case "SALABLE":
                    // Continue with normal processing
                    break;

                default:
                    console.warn("Unknown availability status:", json.availability);
                    break;
            }
        }

        if (json.message?.includes("The product that was requested doesn't exist")) {
            error = {
                type: "NOT_EXISTS",
            };
            if (stateHandlers) {
                stateHandlers.setTicketNotExists(true);
            }
            return [[], json.admissions, response, error];
        }

        // Process successful ticket data
        const admissions = json?.admissions;
        const data = json.tickets
            ? json.tickets.map((ticket) => ({
                  package_product_id: json.package_product_id,
                  ...ticket,
                  ...json.price_config.find((config) => ticket.id === config.id),
                  count: ticket.qty.min,
              }))
            : [];

        return [data, admissions, response, error];
    } catch (fetchError) {
        console.error("Error fetching tickets:", fetchError);
        setError(true);
        error = {
            type: "GENERIC",
        };

        if (stateHandlers) {
            stateHandlers.setSetupError(true);
        }
        return [[], null, "", error];
    }
};
